import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '../../../apiClient';
import { GET_COHORT_QUERY_KEY } from './useGetCohort';
import { GET_COHORT_MEMBERS_QUERY_KEY } from './useGetCohortMembers';

export function useRemoveUserFromCohort(cohortId: string) {
  const queryClient = useQueryClient();
  const apiClient = useApiClient();

  return useMutation({
    mutationFn: async (userId: string) => {
      const response = await apiClient.fetchApi<{}, { success: boolean }>({
        method: 'DELETE',
        endpoint: `cohorts/${cohortId}/members/${userId}`,
      });
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: GET_COHORT_QUERY_KEY(cohortId),
      });
      queryClient.invalidateQueries({
        queryKey: GET_COHORT_MEMBERS_QUERY_KEY(cohortId, undefined, undefined),
      });
    },
  });
}

export type RemoveUserFromCohortParams = { cohortId: string; userId: string };

/**
 * Remove a user from a cohort (both cohortId and userId passed per call, e.g. on user page).
 */
export function useRemoveUserFromCohortMutation() {
  const queryClient = useQueryClient();
  const apiClient = useApiClient();

  return useMutation({
    mutationFn: async ({ cohortId, userId }: RemoveUserFromCohortParams) => {
      const response = await apiClient.fetchApi<{}, { success: boolean }>({
        method: 'DELETE',
        endpoint: `cohorts/${cohortId}/members/${userId}`,
      });
      return response;
    },
    onSuccess: (_, { cohortId, userId }) => {
      queryClient.invalidateQueries({
        queryKey: GET_COHORT_QUERY_KEY(cohortId),
      });
      queryClient.invalidateQueries({
        queryKey: GET_COHORT_MEMBERS_QUERY_KEY(cohortId, undefined, undefined),
      });
      queryClient.invalidateQueries({
        predicate: (query) =>
          Array.isArray(query.queryKey) &&
          query.queryKey[0] === 'cohorts' &&
          query.queryKey[2] === 'user' &&
          query.queryKey[3] === userId,
      });
    },
  });
}
